import React, { Suspense } from 'react';

import EnsembleContextProvider from '../../../../app/context/ensemble-context';
import { SubPage, TabType } from '../../../context/tab-manager-context';
import { Loading } from '../../loading/loading';

import { TabContainerV3 } from './TabContainerV3';
import { TabContentV3 } from './TabContentV3';

import { Panel } from '@controlrooms/components';
import { ViewType } from '@controlrooms/models';

interface MainTabContentProps {
  tab: {
    id: string;
    type: TabType;
    viewType?: ViewType;
    subPage?: SubPage;
    viewId?: string;
  };
}

/**
 * MainTabContent - Right panel (takes remaining width)
 * Contains TabContainerV3 at top, then TabContentV3 below
 * Memoized with custom comparison to prevent re-render when tab object reference changes
 * but properties are the same (e.g., when tabs array is recreated)
 */
export const MainTabContent = React.memo<MainTabContentProps>(
  ({ tab }) => {
    return (
      <EnsembleContextProvider>
        <Panel
          id="mainContent"
          minSize="200px"
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <TabContainerV3 />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              minHeight: 0,
            }}
          >
            <Suspense fallback={<Loading overlay small={true} />}>
              <TabContentV3 tab={tab} />
            </Suspense>
          </div>
        </Panel>
      </EnsembleContextProvider>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if tab properties actually change
    return (
      prevProps.tab.id === nextProps.tab.id &&
      prevProps.tab.type === nextProps.tab.type &&
      prevProps.tab.viewType === nextProps.tab.viewType &&
      prevProps.tab.subPage === nextProps.tab.subPage &&
      prevProps.tab.viewId === nextProps.tab.viewId
    );
  },
);

MainTabContent.displayName = 'MainTabContent';
